import { useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { getRuntimeConfig } from "@/shared/config"
import {
  buildRoomChannel,
  subscribeOnlineCount,
  subscribeRoom,
  subscribeBalance,
  subscribeGiftInventory,
  subscribeTournaments,
} from "@/shared/lib/realtime"
import { apiClient, getBootstrap, getUserGifts } from "@/shared/api"
import {
  getAccessToken,
  isAccessTokenExpired,
  waitForAccountId,
} from "@/shared/api/auth"
import { usePvpRoomsStore } from "@/entities/pvp-roulette"
import { userKeys } from "@/entities/user/hooks"
import { giftKeys, INVENTORY_GIFTS_PARAMS } from "@/entities/gift"
import { paymentHistoryKeys } from "@/entities/history"
import {
  useDemoModeStore,
  useOnlineStore,
  useRoomBootstrapStore,
  useSegmentSheetStore,
  useTournamentStore,
} from "@/shared/store"
import type { Balance, UserGiftsResponse } from "@/shared/api/types"
import { parseSegmentSheetPayload } from "@/shared/lib/parseSegmentSheetPayload"
import { isFeatureEnabled } from "@/shared/features"
import { trackTonDepositCompletedIfApplicable } from "@/shared/lib/analytics"

/**
 * PvpRoomRoot — единая точка подписки и bootstrap'а комнаты.
 * Монтируется один раз на уровень всего приложения и держит состояние комнаты живым.
 */
export function PvpRoomRoot() {
  const config = getRuntimeConfig()
  const roomId = config.pvpRoomId ?? "default"
  const ingestRoundSnapshot = usePvpRoomsStore((s) => s.ingestRoundSnapshot)
  const resetRoom = usePvpRoomsStore((s) => s.resetRoom)
  const setCurrentOnline = useOnlineStore((s) => s.setCurrentOnline)
  const queryClient = useQueryClient()

  useEffect(() => {
    useRoomBootstrapStore.getState().setSubscriptionInitFailed(false)
    useRoomBootstrapStore.getState().setBootstrapReady(false)
    resetRoom(roomId)
    useTournamentStore.getState().reset()

    let isDisposed = false
    let unsubscribeRoomFeed: (() => void) | undefined
    let unsubscribeOnlineCount: (() => void) | undefined
    let unsubscribeBalanceFeed: (() => void) | undefined
    let unsubscribeGiftInventoryFeed: (() => void) | undefined
    let unsubscribeTournamentFeed: (() => void) | undefined

    const initRoomSubscription = async () => {
      if (useDemoModeStore.getState().active) return
      try {
        const authed = await apiClient.ensureSession()
        if (!authed || isDisposed) return

        const accessToken = getAccessToken()
        if (!accessToken || isAccessTokenExpired()) return

        // Warm the single inventory cache (prefetch + WS both use INVENTORY_GIFTS_PARAMS).
        const giftsPrefetch = isFeatureEnabled("gifts")
          ? queryClient.prefetchQuery({
              queryKey: giftKeys.list(INVENTORY_GIFTS_PARAMS),
              queryFn: () => getUserGifts(INVENTORY_GIFTS_PARAMS),
              staleTime: 30_000,
            })
          : Promise.resolve()

        const bootstrap = await getBootstrap(roomId)
        void giftsPrefetch.catch((err) => {
          if (import.meta.env.DEV) {
            console.warn("[PvpRoomRoot] gifts prefetch failed:", err)
          }
        })
        if (isDisposed) return

        if (bootstrap.current_round) {
          ingestRoundSnapshot(roomId, bootstrap.current_round, { source: "api" })
        }

        if (bootstrap.current_balance) {
          const balanceTon = (
            (bootstrap.current_balance.available ??
              bootstrap.current_balance.total) /
            1e9
          ).toString()
          queryClient.setQueryData<Balance>(userKeys.balance(), {
            balance: { amount: balanceTon },
          })
        }

        const segmentPopup = bootstrap.segment_popup
        if (segmentPopup != null && typeof segmentPopup === "object") {
          const parsed = parseSegmentSheetPayload(segmentPopup)
          if (parsed.data) {
            useSegmentSheetStore.getState().show(parsed.data)
          } else if (import.meta.env.DEV) {
            console.warn("[PvpRoomRoot] segment_popup ignored:", parsed.error)
          }
        }
        if (isFeatureEnabled("tournaments")) {
          // Bootstrap: take `items[0]` from `tournaments` (we display a
          // single tournament for now; multi-tournament UI is TBD).
          useTournamentStore
            .getState()
            .ingestBootstrapItems(bootstrap.tournaments_items)
        }

        useRoomBootstrapStore.getState().setBootstrapReady(true)

        // Room + online — стартуют сразу, не зависят от account ID
        unsubscribeRoomFeed = subscribeRoom(roomId, {
          onSnapshot: (snap, source, clockOffsetMs) => {
            ingestRoundSnapshot(roomId, snap, { source, clockOffsetMs })
          },
          onError: (err) =>
            console.error("[PvpRoomRoot] Centrifugo error:", err),
        }, { skipApiFetch: true })

        unsubscribeOnlineCount = subscribeOnlineCount(
          buildRoomChannel(roomId),
          {
            onCount: setCurrentOnline,
            onError: (err) =>
              console.error("[PvpRoomRoot] Online count error:", err),
          }
        )

        if (isFeatureEnabled("tournaments")) {
          unsubscribeTournamentFeed = subscribeTournaments({
            onEvent: (event) => {
              useTournamentStore
                .getState()
                .ingestRealtimeUpdate(event.tournament)
            },
          })
        }

        // Balance — ждёт account ID параллельно, не блокирует комнату.
        // waitForAccountId() резолвится когда setAccountId() вызывается из ответа авторизации.
        void waitForAccountId().then((accountId) => {
          if (isDisposed) return

          const applyBalanceUpdate = (newBalanceNano: number) => {
            const cached = queryClient.getQueryData<Balance>(userKeys.balance())
            const previousBalanceNano = cached
              ? Math.round(Number(cached.balance.amount) * 1e9)
              : null

            trackTonDepositCompletedIfApplicable(
              previousBalanceNano,
              newBalanceNano
            )

            const newBalanceTon = (newBalanceNano / 1e9).toString()
            queryClient.setQueryData<Balance>(userKeys.balance(), (old) => {
              if (!old) return old
              return {
                ...old,
                balance: { ...old.balance, amount: newBalanceTon },
              }
            })
            void queryClient.invalidateQueries({
              queryKey: paymentHistoryKeys.infinite(),
            })
          }

          unsubscribeBalanceFeed = subscribeBalance(accountId, {
            onEvent: (event) => {
              if (event.asset_class !== "TON") return
              applyBalanceUpdate(event.new_balance)
            },
            onReconnect: () => {
              void queryClient.invalidateQueries({
                queryKey: userKeys.balance(),
              })
            },
          })

          if (isFeatureEnabled("gifts")) {
            const giftsCacheKey = giftKeys.list(INVENTORY_GIFTS_PARAMS)
            unsubscribeGiftInventoryFeed = subscribeGiftInventory(accountId, {
              onEvent: (event) => {
                queryClient.setQueryData<UserGiftsResponse>(giftsCacheKey, {
                  items: event.items,
                  next_cursor: null,
                })
              },
              onReconnect: () => {
                void queryClient.invalidateQueries({ queryKey: giftKeys.all })
              },
            })
          }
        })
      } catch (e) {
        console.error(
          "[PvpRoomRoot] Failed to initialize room subscription:",
          e
        )
        useRoomBootstrapStore.getState().setSubscriptionInitFailed(true)
      }
    }

    void initRoomSubscription()

    return () => {
      isDisposed = true
      useRoomBootstrapStore.getState().setBootstrapReady(false)
      unsubscribeOnlineCount?.()
      unsubscribeRoomFeed?.()
      unsubscribeBalanceFeed?.()
      unsubscribeGiftInventoryFeed?.()
      unsubscribeTournamentFeed?.()
      useTournamentStore.getState().reset()
      setCurrentOnline(null)
    }
  }, [roomId, ingestRoundSnapshot, queryClient, resetRoom, setCurrentOnline])

  return null
}
